/**
 * Matrice de compatibilité réseau × type de diffusion.
 *
 * Source de vérité unique (app + edge fns + tests). Reflète les contraintes des
 * éditeurs d'origine : un défi se diffuse en YouTube Short / TikTok, un replay
 * d'exercice en reels (Insta/TikTok/FB) + YouTube, un clip playlist / vidéo
 * longue en YouTube, un article de blog en LinkedIn / Facebook / Instagram.
 */

import type { DiffusionKind, DiffusionPlatform, DiffusionType } from './types';

export const VIDEO_PLATFORMS: readonly DiffusionPlatform[] = [
  'youtube',
  'tiktok',
  'instagram_reels',
  'facebook_reels',
] as const;

export const BLOG_PLATFORMS: readonly DiffusionPlatform[] = [
  'linkedin',
  'facebook',
  'instagram',
] as const;

export const DIFFUSION_TYPES: readonly DiffusionType[] = [
  'challenge',
  'exercise_replay',
  'playlist',
  'exercise_video',
  'blog',
] as const;

const PLATFORMS_BY_TYPE: Record<DiffusionType, readonly DiffusionPlatform[]> = {
  challenge: ['youtube', 'tiktok'],
  exercise_replay: ['instagram_reels', 'tiktok', 'facebook_reels', 'youtube'],
  playlist: ['youtube'],
  exercise_video: ['youtube'],
  blog: ['linkedin', 'facebook', 'instagram'],
};

/** Le contenu d'un type est-il une vidéo (rendue par le worker) ou un post blog ? */
export function kindForType(type: DiffusionType): DiffusionKind {
  return type === 'blog' ? 'blog' : 'video';
}

/** Réseau → vidéo (reels/yt) ou blog (feed). */
export function kindForPlatform(platform: DiffusionPlatform): DiffusionKind {
  return (BLOG_PLATFORMS as readonly DiffusionPlatform[]).includes(platform) ? 'blog' : 'video';
}

/** Réseaux valides pour un type de diffusion donné. */
export function platformsForType(type: DiffusionType): readonly DiffusionPlatform[] {
  return PLATFORMS_BY_TYPE[type];
}

export function isPlatformValidForType(type: DiffusionType, platform: DiffusionPlatform): boolean {
  return platformsForType(type).includes(platform);
}
